import {Link, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {LinkStyle} from '@ui/buttons/external-link'; 
import {RegisterPayload, useRegister} from '../requests/use-register';
import {useEmailAvailability} from '../requests/use-email-availability';
import {useUsernameAvailability} from '../requests/use-username-availability';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {message as i18nMessage} from '@ui/i18n/message';
import {FormCheckbox} from '@ui/forms/toggle/checkbox';
import {CustomMenuItem} from '../../menus/custom-menu';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '@ui/settings/use-settings';
import {useContext, useEffect, useState} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {IconButton} from '@ui/buttons/icon-button';
import {VisibilityIcon} from '@ui/icons/material/Visibility';
import {VisibilityOffIcon} from '@ui/icons/material/VisibilityOff';
import {CheckCircleOutlineIcon} from '@ui/icons/material/CheckCircleOutline';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {AirplaneTicketIcon} from '@ui/icons/material/AirplaneTicket';
import {KeyboardArrowDownIcon} from '@ui/icons/material/KeyboardArrowDown';
import {KeyboardArrowUpIcon} from '@ui/icons/material/KeyboardArrowUp';
import {usePreviewRegisterVoucher, PreviewRegisterVoucherResponse} from '../requests/use-preview-register-voucher';
import {toast} from '@ui/toast/toast';
import {useNavigate} from '../../ui/navigation/use-navigate';
import {FormattedCurrency} from '@ui/i18n/formatted-currency';
import {useAuth} from '@common/auth/use-auth';
import {consumeAuthRedirectUri} from '@common/auth/auth-redirect-uri';
import {getMusicPreferenceRedirectUri} from '@app/web-player/onboarding/music-preference/redirect';
import {
  ResolvedCustomAuthPage,
  ResolvedCustomAuthPlan,
  formatTrialPeriodLabel,
} from '@app/custom-auth-pages/custom-auth-page';

interface RegisterPageProps {
  customAuthPage?: ResolvedCustomAuthPage;
}

export function RegisterPage({customAuthPage}: RegisterPageProps = {}) {
  const {
    branding,
    registration: {disable},
    social,
  } = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const {verify, isVerifying} = useRecaptcha('register');
  const {trans} = useTrans();

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();

  const isWorkspaceRegister = pathname.includes('workspace');
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;
  const referrerCode = searchParams.get('ref') || undefined;

  const form = useForm<RegisterPayload>({
    defaultValues: {
      email: searchParamsEmail,
      referrer_code: referrerCode,
    },
  });
  const navigate = useNavigate();
  const {getRedirectUri} = useAuth();
  const [selectedCustomPlanKey, setSelectedCustomPlanKey] = useState(
    () => customAuthPage?.plans?.[0] ? customPlanKey(customAuthPage.plans[0]) : '',
  );
  const selectedCustomPlan =
    customAuthPage?.plans.find(plan => customPlanKey(plan) === selectedCustomPlanKey) ||
    customAuthPage?.plans?.[0] ||
    null;

  useEffect(() => {
    if (customAuthPage?.plans?.[0]) {
      setSelectedCustomPlanKey(customPlanKey(customAuthPage.plans[0]));
    }
  }, [customAuthPage?.id, customAuthPage?.plans]);

  const register = useRegister(form, {
    onRegistered: async () => {
      if (customAuthPage) {
        if (selectedCustomPlan) {
          consumeAuthRedirectUri();
          window.location.href = selectedCustomPlan.checkout_path;
          return true;
        }

        consumeAuthRedirectUri();
        navigate('/');
        return true;
      }

      // After register, if voucher is 100%, create free subscription and redirect to success
      if (voucherPreview?.valid && voucherPreview.voucher && voucherPreview.plan) {
        const percent = voucherPreview.voucher.discount_percentage;
        try {
          if (percent === 100) {
            // create free subscription immediately
            await fetchFreeSubscription(voucherPreview);
            window.location.href = getMusicPreferenceRedirectUri(
              '/checkout/voucher/success',
            );
            return true; // handled navigation
          } else {
            // redirect user to checkout of that plan with hint to apply code
            toast.positive('Voucher válido! Aplique o desconto na próxima etapa.');
            navigate(`/checkout/${voucherPreview.plan.product_id}/${voucherPreview.plan.price_id}?voucher=${encodeURIComponent(voucherPreview.voucher.code)}`);
            return true; // handled navigation
          }
        } catch (e: any) {
          toast.danger(e?.response?.data?.message || 'Erro ao aplicar voucher após cadastro');
          return false; // fall back to default navigation
        }
      }
      // No voucher applied on register page: do NOT send user to checkout
      // Handle navigation explicitly to home (or another safe default)
      navigate(
        getMusicPreferenceRedirectUri(
          consumeAuthRedirectUri() || getRedirectUri(),
        ),
        {replace: true},
      );
      return true; // prevent default navigation that could use onboarding -> checkout
    },
  });
  const previewVoucher = usePreviewRegisterVoucher();
  const [isVoucherExpanded, setIsVoucherExpanded] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherPreview, setVoucherPreview] = useState<PreviewRegisterVoucherResponse | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const emailValue = form.watch('email') || '';
  const normalizedEmail = emailValue.trim();
  const debouncedEmail = useDebouncedValue(normalizedEmail, 450);
  const emailHasInput = normalizedEmail.length > 0;
  const emailHasValidFormat = isValidEmail(normalizedEmail);
  const emailAvailability = useEmailAvailability(
    debouncedEmail,
    debouncedEmail.length > 0 && isValidEmail(debouncedEmail),
  );
  const emailStatus =
    emailAvailability.data?.email === debouncedEmail
      ? emailAvailability.data
      : undefined;
  const emailCheckIsPending =
    emailHasInput &&
    emailHasValidFormat &&
    (normalizedEmail !== debouncedEmail || emailAvailability.isFetching);
  const isEmailAvailable = !!(
    emailStatus?.valid &&
    emailStatus.available &&
    normalizedEmail === debouncedEmail
  );
  const emailBlocksSubmit = !!(
    emailHasInput &&
    (!emailHasValidFormat || (emailStatus && !emailStatus.available))
  );
  const usernameValue = form.watch('username') || '';
  const debouncedUsername = useDebouncedValue(usernameValue, 450);
  const usernameAvailability = useUsernameAvailability(
    debouncedUsername,
    debouncedUsername.length > 0,
  );
  const usernameStatus =
    usernameAvailability.data?.username === debouncedUsername
      ? usernameAvailability.data
      : undefined;
  const usernameHasInput = usernameValue.length > 0;
  const usernameCheckIsPending =
    usernameHasInput &&
    (usernameValue !== debouncedUsername || usernameAvailability.isFetching);
  const isUsernameAvailable = !!(
    usernameStatus?.valid &&
    usernameStatus.available &&
    usernameValue === debouncedUsername
  );
  const usernameBlocksSubmit = !!(
    usernameStatus &&
    (!usernameStatus.valid || !usernameStatus.available)
  );
  const phoneValue = form.watch('phone') || '';
  const isPhoneComplete = isValidPhoneNumber(phoneValue);
  const passwordValue = form.watch('password') || '';
  const passwordConfirmationValue = form.watch('password_confirmation') || '';
  const passwordsMatch =
    passwordValue.length >= 5 && passwordValue === passwordConfirmationValue;

  useEffect(() => {
    if (!emailHasInput) {
      form.clearErrors('email');
      return;
    }

    if (normalizedEmail !== debouncedEmail) {
      form.clearErrors('email');
      return;
    }

    if (!emailHasValidFormat) {
      form.setError('email', {
        type: 'manual',
        message: trans(i18nMessage('Enter a valid email address.')),
      });
      return;
    }

    if (!emailStatus) {
      return;
    }

    if (emailStatus.valid && emailStatus.available) {
      form.clearErrors('email');
    } else if (emailStatus.message) {
      form.setError('email', {
        type: 'manual',
        message: emailStatus.message,
      });
    }
  }, [
    debouncedEmail,
    emailHasInput,
    emailHasValidFormat,
    emailStatus,
    form,
    normalizedEmail,
    trans,
  ]);

  useEffect(() => {
    if (!usernameValue) {
      form.clearErrors('username');
      return;
    }

    if (!usernameStatus || usernameValue !== debouncedUsername) {
      return;
    }

    if (usernameStatus.valid && usernameStatus.available) {
      form.clearErrors('username');
    } else if (usernameStatus.message) {
      form.setError('username', {
        type: 'manual',
        message: usernameStatus.message,
      });
    }
  }, [debouncedUsername, form, usernameStatus, usernameValue]);

  if (disable) {
    return <Navigate to={customAuthPage?.login_path || '/login'} replace />;
  }

async function fetchFreeSubscription(preview: PreviewRegisterVoucherResponse) {
  // Defensive checks
  if (!preview.voucher || !preview.plan) return;
  const {voucher, plan} = preview;
  // Use same endpoint as checkout for free vouchers
  const res = await fetch('/api/v1/vouchers/create-free-subscription', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      product_id: plan.product_id,
      price_id: plan.price_id,
      voucher_id: voucher.id,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message || 'Erro ao criar assinatura gratuita');
  }
}

function handleApplyVoucher() {
  const code = voucherCode.trim().toUpperCase();
  if (!code) return;
  previewVoucher.mutate(
    {voucher_code: code},
    {
      onSuccess: data => {
        setVoucherPreview(data);
        if (data.valid) {
          toast.positive('Voucher válido!');
        } else {
          toast.danger(data.message || 'Voucher inválido.');
        }
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 'Erro ao validar voucher';
        setVoucherPreview(null);
        toast.danger(message);
      },
    }
  );
}

  let heading = <Trans message="Create a new account" />;
  if (customAuthPage) {
    heading = <>Criar conta em {customAuthPage.entity_name}</>;
  } else if (isWorkspaceRegister) {
    heading = (
      <Trans
        values={{siteName: branding?.site_name}}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  } else if (referrerCode) {
    heading = <Trans message="Create an account - Referred by a friend" />;
  }

  const message = (
    <Trans
      values={{
        a: parts => (
          <Link className={LinkStyle} to={customAuthPage?.login_path || '/login'}>
            {parts}
          </Link>
        ),
      }}
      message="Already have an account? <a>Sign in.</a>"
    />
  );

  return (
    <AuthLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Register" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={async payload => {
          if (emailCheckIsPending || usernameCheckIsPending) {
            return;
          }

          if (emailHasInput && !emailHasValidFormat) {
            form.setError('email', {
              type: 'manual',
              message: trans(i18nMessage('Enter a valid email address.')),
            });
            return;
          }

          if (emailStatus && !emailStatus.available) {
            form.setError('email', {
              type: 'manual',
              message: emailStatus.message,
            });
            return;
          }

          if (usernameStatus && !usernameStatus.available) {
            form.setError('username', {
              type: 'manual',
              message: usernameStatus.message,
            });
            return;
          }

          if (customAuthPage && !selectedCustomPlan) {
            toast.danger('Selecione um plano para continuar.');
            return;
          }

          const isValid = await verify();
          if (isValid) {
            register.mutate(payload);
          }
        }}
      >
        <FormTextField
          className="mb-10"
          name="email"
          type="email"
          disabled={!!searchParamsEmail}
          label={<Trans message="Email" />}
          inputBorder={isEmailAvailable ? 'border-positive border' : undefined}
          endAdornment={
            isEmailAvailable ? (
              <CheckCircleOutlineIcon className="text-positive" size="sm" />
            ) : undefined
          }
          required
        />
        <div className="mb-32 text-xs">
          {isEmailAvailable ? (
            <div className="flex items-center gap-6 text-positive">
              <CheckCircleOutlineIcon size="xs" />
              <Trans message="Email is available." />
            </div>
          ) : emailCheckIsPending ? (
            <div className="text-muted">
              <Trans message="Checking email..." />
            </div>
          ) : null}
        </div>
        <FormTextField
          className="mb-10"
          name="username"
          type="text"
          label={<Trans message="Username" />}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          inputBorder={isUsernameAvailable ? 'border-positive border' : undefined}
          endAdornment={
            isUsernameAvailable ? (
              <CheckCircleOutlineIcon className="text-positive" size="sm" />
            ) : undefined
          }
          required
        />
        <div className="mb-32 text-xs">
          {isUsernameAvailable ? (
            <div className="flex items-center gap-6 text-positive">
              <CheckCircleOutlineIcon size="xs" />
              <Trans message="Username is available." />
            </div>
          ) : usernameCheckIsPending ? (
            <div className="text-muted">
              <Trans message="Checking username..." />
            </div>
          ) : usernameStatus?.suggestions?.length ? (
            <div>
              <div className="text-muted">
                <Trans message="Try one of these available usernames:" />
              </div>
              <div className="mt-8 flex flex-wrap gap-8">
                {usernameStatus.suggestions.map(suggestion => (
                  <button
                    key={suggestion}
                    type="button"
                    className="rounded-full border border-divider px-10 py-4 text-xs text-foreground transition-colors hover:border-primary hover:text-primary"
                    onClick={() => {
                      form.setValue('username', suggestion, {
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                      form.clearErrors('username');
                    }}
                  >
                    @{suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-muted">
              <Trans message="Use letters, numbers, and underscores only. No spaces or hyphens." />
            </div>
          )}
        </div>
        <FormTextField
          className="mb-32"
          name="phone"
          type="text"
          label={<Trans message="Phone number" />}
          pattern="[0-9 +()-]{10,20}"
          inputBorder={isPhoneComplete ? 'border-positive border' : undefined}
          endAdornment={
            isPhoneComplete ? (
              <CheckCircleOutlineIcon className="text-positive" size="sm" />
            ) : undefined
          }
          required
        />
        <FormTextField
          className="mb-32"
          name="password"
          type={showPassword ? 'text' : 'password'}
          label={<Trans message="Password" />}
          inputBorder={passwordsMatch ? 'border-positive border' : undefined}
          required
          endAdornment={
            <div className="pointer-events-auto flex items-center gap-6 pr-6">
              {passwordsMatch && (
                <CheckCircleOutlineIcon className="text-positive" size="sm" />
              )}
              <IconButton
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                onMouseDown={e => e.preventDefault()}
                onClick={() => setShowPassword(v => !v)}
                type="button"
                variant="text"
                size="sm"
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </div>
          }
        />
        <FormTextField
          className="mb-32"
          name="password_confirmation"
          type={showPasswordConfirm ? 'text' : 'password'}
          label={<Trans message="Confirm password" />}
          inputBorder={passwordsMatch ? 'border-positive border' : undefined}
          required
          endAdornment={
            <div className="pointer-events-auto flex items-center gap-6 pr-6">
              {passwordsMatch && (
                <CheckCircleOutlineIcon className="text-positive" size="sm" />
              )}
              <IconButton
                aria-label={
                  showPasswordConfirm
                    ? 'Ocultar confirmação'
                    : 'Mostrar confirmação'
                }
                onMouseDown={e => e.preventDefault()}
                onClick={() => setShowPasswordConfirm(v => !v)}
                type="button"
                variant="text"
                size="sm"
              >
                {showPasswordConfirm ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )}
              </IconButton>
            </div>
          }
        />
        {customAuthPage && (
          <CustomPlanSelector
            plans={customAuthPage.plans}
            selectedPlanKey={selectedCustomPlan ? customPlanKey(selectedCustomPlan) : ''}
            onChange={setSelectedCustomPlanKey}
          />
        )}
        {!customAuthPage && (
        <div className="mb-16 mt-8">
          <button
            type="button"
            onClick={() => setIsVoucherExpanded(v => !v)}
            className="flex w-full items-center justify-between rounded-lg border border-divider bg-background p-12 text-left transition-colors hover:bg-hover"
          >
            <div className="flex items-center gap-10">
              <AirplaneTicketIcon className="text-primary" size="sm" />
              <span className="text-sm font-medium">
                <Trans message="Tem cupom de desconto?" />
              </span>
            </div>
            {isVoucherExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </button>

          {isVoucherExpanded && (
            <div className="mt-12 flex items-center gap-8">
              <TextField
                placeholder="Código do voucher"
                value={voucherCode}
                onChange={e => setVoucherCode(e.target.value)}
                className="flex-1"
                onKeyDown={e => {
                  if (e.key === 'Enter') handleApplyVoucher();
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleApplyVoucher}
                disabled={previewVoucher.isPending || !voucherCode.trim()}
              >
                {previewVoucher.isPending ? <Trans message="Validando..." /> : <Trans message="Aplicar" />}
              </Button>
            </div>
          )}

          {voucherPreview && (
            <div
              className={`mt-12 rounded-lg border p-12 ${voucherPreview.valid ? 'border-positive bg-positive/5' : 'border-danger bg-danger/5'}`}
            >
              <div className="text-sm">
                {voucherPreview.message}
              </div>
              {voucherPreview.valid && (
                <div className="mt-10 space-y-12 text-xs text-muted">
                  <div>
                    <Trans message="Plano" />: {voucherPreview.plan?.product_name} (
                    {voucherPreview.plan?.interval})
                  </div>
                  <div>
                    <Trans message="Desconto" />: {voucherPreview.voucher?.discount_percentage}%
                  </div>
                  {voucherPreview.pricing && (
                    <div className="space-y-8 rounded-lg bg-background/60 p-12 text-sm text-foreground">
                      <div className="flex items-center justify-between gap-24 text-muted">
                        <div>
                          <Trans message="Original price" />
                        </div>
                        <div className="line-through">
                          <FormattedCurrency
                            value={voucherPreview.pricing.original_price}
                            currency={voucherPreview.pricing.currency || 'BRL'}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-24 text-positive">
                        <div>
                          <Trans message="Discount" /> ({voucherPreview.pricing.discount_percentage}%)
                        </div>
                        <div>
                          -
                          <FormattedCurrency
                            value={voucherPreview.pricing.discount_amount}
                            currency={voucherPreview.pricing.currency || 'BRL'}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-24 font-medium text-lg">
                        <div>
                          <Trans
                            message={
                              voucherPreview.pricing.final_price === 0
                                ? 'Total (Free)'
                                : 'Billed today'
                            }
                          />
                        </div>
                        <div
                          className={
                            voucherPreview.pricing.final_price === 0 ? 'text-positive' : undefined
                          }
                        >
                          {voucherPreview.pricing.final_price === 0 ? (
                            <Trans message="FREE" />
                          ) : (
                            <FormattedCurrency
                              value={voucherPreview.pricing.final_price}
                              currency={voucherPreview.pricing.currency || 'BRL'}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}
        {(() => {
          const RegisterFields = (auth as any)?.registerFields;
          return RegisterFields ? <RegisterFields /> : null;
        })()}
        <PolicyCheckboxes />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={
            register.isPending ||
            isVerifying ||
            emailCheckIsPending ||
            emailBlocksSubmit ||
            usernameCheckIsPending ||
            usernameBlocksSubmit
          }
        >
          <Trans message="Create account" />
        </Button>
        <SocialAuthSection
          dividerMessage={
            social.compact_buttons ? (
              <Trans message="Or sign up with" />
            ) : (
              <Trans message="OR" />
            )
          }
        />
      </Form>
    </AuthLayout>
  );
}

interface CustomPlanSelectorProps {
  plans: ResolvedCustomAuthPlan[];
  selectedPlanKey: string;
  onChange: (key: string) => void;
}

function CustomPlanSelector({
  plans,
  selectedPlanKey,
  onChange,
}: CustomPlanSelectorProps) {
  if (!plans.length) {
    return (
      <div className="mb-24 rounded border border-danger bg-danger/5 p-12 text-sm text-danger">
        Nenhum plano foi configurado para esta página.
      </div>
    );
  }

  const selectedPlan =
    plans.find(p => customPlanKey(p) === selectedPlanKey) || plans[0] || null;
  const trialDays = selectedPlan?.trial_period || 0;

  const trialEndDate = new Date();
  if (trialDays > 0) {
    trialEndDate.setDate(trialEndDate.getDate() + trialDays);
  }

  return (
    <div className="mb-24">
      <div className="mb-10 text-sm font-medium">Selecione seu plano</div>
      <div className="space-y-10">
        {plans.map(plan => {
          const key = customPlanKey(plan);
          const selected = key === selectedPlanKey;
          const hasTrial = Number(plan.trial_period || 0) > 0;

          return (
            <button
              key={key}
              type="button"
              className={`w-full rounded-lg border p-14 text-left transition-all ${
                selected
                  ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20'
                  : 'border-divider bg-background hover:bg-hover'
              }`}
              onClick={() => onChange(key)}
            >
              <div className="flex items-center justify-between gap-16">
                <div>
                  <div className="flex flex-wrap items-center gap-8">
                    <span className="text-sm font-semibold">
                      {plan.product_name}
                    </span>
                    {hasTrial && (
                      <span className="inline-flex items-center rounded-full bg-primary/15 px-8 py-2 text-xs font-semibold text-primary">
                        Free por {formatTrialPeriodLabel(plan.trial_period!)}
                      </span>
                    )}
                  </div>
                  {plan.product_description && (
                    <div className="mt-4 text-xs text-muted">
                      {plan.product_description}
                    </div>
                  )}
                  {hasTrial ? (
                    <div className="mt-6 text-xs text-primary/80 font-medium">
                      Cobrança somente após o período de teste
                    </div>
                  ) : (
                    <div className="mt-6 text-xs text-muted">
                      Cobrança imediata no checkout
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  {hasTrial ? (
                    <div>
                      <div className="text-xs font-semibold text-positive uppercase tracking-wide">
                        Grátis hoje
                      </div>
                      <div className="text-xs text-muted">
                        depois{' '}
                        <FormattedCurrency
                          value={Number(plan.amount)}
                          currency={plan.currency || 'BRL'}
                        />
                        {plan.interval ? ` / ${plan.interval}` : ''}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm font-semibold">
                      <FormattedCurrency
                        value={Number(plan.amount)}
                        currency={plan.currency || 'BRL'}
                      />
                      {plan.interval && (
                        <div className="text-xs font-normal text-muted">
                          {plan.interval}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedPlan && trialDays > 0 && (
        <div className="mt-12 rounded-lg border border-primary/30 bg-primary/5 p-12 text-xs text-foreground">
          <div className="flex items-start gap-10">
            <svg
              className="mt-2 h-18 w-18 flex-shrink-0 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <div className="font-semibold text-primary">
                Carência ativada: {formatTrialPeriodLabel(trialDays)} grátis
              </div>
              <p className="mt-2 text-muted leading-relaxed">
                Você não será cobrado no momento do cadastro. O primeiro débito de{' '}
                <span className="font-medium text-foreground">
                  <FormattedCurrency
                    value={Number(selectedPlan.amount)}
                    currency={selectedPlan.currency || 'BRL'}
                  />
                  {selectedPlan.interval ? ` / ${selectedPlan.interval}` : ''}
                </span>{' '}
                será realizado apenas em{' '}
                <span className="font-medium text-foreground">
                  {trialEndDate.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
                . Na próxima etapa, você adicionará seu cartão de crédito com segurança para validar o início da sua carência.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function customPlanKey(plan: ResolvedCustomAuthPlan): string {
  return `${plan.product_id}:${plan.price_id}`;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => window.clearTimeout(timeout);
  }, [delay, value]);

  return debouncedValue;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhoneNumber(phone: string): boolean {
  return phone.length >= 10 && phone.length <= 20 && /^[0-9 +()-]+$/.test(phone);
}

function PolicyCheckboxes() {
  const {
    registration: {policies},
  } = useSettings();

  if (!policies) return null;

  return (
    <div className="mb-32">
      {policies.map(policy => (
        <FormCheckbox
          key={policy.id}
          name={policy.id}
          className="mb-4 block"
          required
        >
          <Trans
            message="I accept the :name"
            values={{
              name: (
                <CustomMenuItem
                  unstyled
                  className={() => LinkStyle}
                  item={policy}
                />
              ),
            }}
          />
        </FormCheckbox>
      ))}
    </div>
  );
}
